/**
 * Which language the app is in, and -- the part that matters -- whether anybody actually
 * chose it.
 *
 * ## Three states, and why the browser needs a second key
 *
 * The server distinguishes "this person chose Romanian", "their clinic prefers Romanian" and
 * "nobody has said". The browser has to make the same distinction, and `i18nextLng` cannot:
 * `i18next-browser-languagedetector` writes it during i18next's own `init`, so a value
 * *guessed* from `navigator.language` is stored exactly as a value the header picker set. By
 * the time anything reads it the two are indistinguishable.
 *
 * So an explicit choice gets a key of its own. It holds the code rather than a boolean --
 * self-describing in devtools, and it lets the app re-assert the choice if `i18nextLng` is
 * ever cleared by something else.
 *
 * Not to be confused with `invoicingSettings.language`, which is the language of the *invoice
 * document* a clinic issues, defaults to `'RO'` and has nothing to do with any of this.
 */

export const SUPPORTED_LANGUAGES = ['ro', 'en'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** Where an explicit choice is remembered. Deliberately not i18next's own key. */
const CHOICE_KEY = 'carefull:languageChoice';

/** i18next's detector cache, read once by the migration below and never written here. */
const DETECTOR_KEY = 'i18nextLng';

export const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
    typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/**
 * The language this browser's owner picked, or null if they never have.
 *
 * Total: a key holding something unsupported reads as no choice rather than throwing, so a
 * hand-edited localStorage costs a fallback and never a blank page.
 */
export const readLanguageChoice = (): SupportedLanguage | null => {
    try {
        const stored = window.localStorage.getItem(CHOICE_KEY);

        return isSupportedLanguage(stored) ? stored : null;
    } catch {
        // Safari in private mode throws on localStorage. Reading no choice is the right
        // answer there: the detector still works, nothing is remembered, nothing breaks.
        return null;
    }
};

export const writeLanguageChoice = (language: SupportedLanguage): void => {
    try {
        window.localStorage.setItem(CHOICE_KEY, language);
    } catch {
        // As above. A preference that cannot be remembered is not a reason to fail a click.
    }
};

/**
 * Treats everybody who has already used the app as having chosen.
 *
 * Without this, the clinic default would reach exactly the people it must not. A Romanian
 * user with `i18nextLng: 'ro'` visiting a clinic whose default is `en` has no marker, so
 * step 3 of `useLanguagePreference` would apply the clinic's default and flip them to
 * English on their next load -- which is, arriving from a new direction, the bug the long
 * comment in `src/i18n.js` was written about. Anyone who has ever loaded the app has an
 * `i18nextLng`; a genuinely new visitor does not, because this runs before i18next writes one.
 *
 * **Must be called before i18next's `init()`**, which is what creates `i18nextLng` -- hence
 * the call at the top of `src/i18n.js` rather than in a component. The logic is here so it
 * can be tested: Jest maps that module away, so the call site never runs under test.
 */
export const grandfatherLanguageChoice = (): void => {
    try {
        if (window.localStorage.getItem(CHOICE_KEY)) {
            return;
        }

        const detected = window.localStorage.getItem(DETECTOR_KEY);

        if (isSupportedLanguage(detected)) {
            window.localStorage.setItem(CHOICE_KEY, detected);
        }
    } catch {
        // As above.
    }
};
